/*
 * Pure calendar math for the scheduling module.
 *
 * No module-local state and no DOM touching: every function takes whatever
 * "where are we" values it needs as arguments. The calling component owns
 * the view/offset state (via useState or a Zustand store) and passes them
 * into these helpers. The legacy calendar.js kept ANCHOR + calView +
 * calOffset in module scope; re-entrancy, testability, and SSR all suffered
 * for it.
 */

/** Calendar view modes. Workweek = Mon–Fri, week = Sun–Sat, day = 1 date. */
export type CalendarView = 'workweek' | 'week' | 'day';

/**
 * Canonical session statuses in the React rebuild. `'No Show'` from the
 * legacy app was dropped — No Shows are recorded as `'Canceled'` with a
 * matching cancel_reason entry. The legacy split between
 * `'Canceled - Staff'` and `'Canceled - Client'` was also collapsed
 * (2026-04-19): the cancel reason carries the who-canceled information,
 * so a single `'Canceled'` status plus a required reason replaces two
 * statuses that differed only in attribution.
 */
export const SESSION_STATUSES = [
    'Scheduled',
    'Complete',
    'Finalized',
    'Canceled',
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/** Minimum shape a session needs to be placed on the calendar grid. */
export interface LayoutInput {
    startH: number;
    startM: number;
    /** Duration in minutes. */
    dur: number;
}

/**
 * Returns the Monday of the work week containing `now`, at midnight local
 * time. Used as the reference point for all week-offset arithmetic. Accepts
 * a `now` argument so tests can pin a deterministic date.
 */
export function anchorMonday(now: Date = new Date()): Date {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    const dow = d.getDay(); // 0 = Sun
    d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
    return d;
}

/**
 * Returns the dates covered by the current calendar view. For workweek and
 * week modes the returned array has 5 and 7 dates respectively. Day mode
 * returns a single-element array; callers that only need one date can use
 * `dayDate` directly.
 *
 * The period returned is the one *containing* `anchor` (then shifted by
 * `offset`). This matters on a Sunday: the Sun–Sat week starting that day is
 * the *current* week, not the one that just ended — so passing today's date
 * (any weekday) yields a view that always includes today at `offset === 0`.
 *
 * `offset` counts weeks (workweek/week) or days (day). Negative values go
 * back in time.
 */
export function weekDates(anchor: Date, offset: number, view: CalendarView): Date[] {
    if (view === 'day') {
        return [dayDate(anchor, offset)];
    }
    if (view === 'week') {
        // Sun–Sat week containing `anchor`. getDay() 0 = Sun, so subtracting it
        // lands on this week's Sunday — on a Sunday that's the same day.
        const sunday = new Date(anchor);
        sunday.setHours(0, 0, 0, 0);
        sunday.setDate(anchor.getDate() - anchor.getDay() + offset * 7);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(sunday);
            d.setDate(sunday.getDate() + i);
            return d;
        });
    }
    // workweek: Mon–Fri of the *same Sun–Sat week* the `week` view shows, so
    // toggling between the two never jumps weeks. The Monday is the day after
    // that week's Sunday — including on a Sunday, where the workweek is the
    // Mon–Fri that Sunday opens, not the one that just ended. (This is the one
    // day the old anchorMonday "roll back to previous Monday" rule disagreed
    // with the Sun-start week and made the toggle jump.)
    const monday = new Date(anchor);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(anchor.getDate() - anchor.getDay() + 1 + offset * 7);
    return Array.from({ length: 5 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return d;
    });
}

/**
 * Default calendar view for a given day. On weekends we open the full Sun–Sat
 * week so the current day is always on screen — the Mon–Fri workweek would
 * hide a Saturday or Sunday entirely. Weekdays default to the tighter workweek.
 */
export function defaultView(now: Date = new Date()): CalendarView {
    const dow = now.getDay(); // 0 = Sun, 6 = Sat
    return dow === 0 || dow === 6 ? 'week' : 'workweek';
}

/** A date that falls inside the period currently shown — today when it's
 *  visible, otherwise the first day of the period. Used to translate the
 *  offset across a view switch without snapping back to the current week. */
function periodDate(today: Date, offset: number, view: CalendarView): Date {
    const days = weekDates(today, offset, view);
    const match = days.find(
        (d) =>
            d.getFullYear() === today.getFullYear()
            && d.getMonth() === today.getMonth()
            && d.getDate() === today.getDate(),
    );
    return match ?? days[0];
}

/** The `offset` (in `view`'s native unit) that makes `view` display the period
 *  containing `target`, measured from the period containing `today`. */
function offsetForDate(today: Date, target: Date, view: CalendarView): number {
    const DAY_MS = 86_400_000;
    if (view === 'day') {
        const a = new Date(today); a.setHours(0, 0, 0, 0);
        const b = new Date(target); b.setHours(0, 0, 0, 0);
        return Math.round((b.getTime() - a.getTime()) / DAY_MS);
    }
    // Difference, in whole weeks, between the period-starts that contain each
    // date. Math.round absorbs the ±1h a DST boundary adds to the span.
    const a = weekDates(today, 0, view)[0];
    const b = weekDates(target, 0, view)[0];
    return Math.round((b.getTime() - a.getTime()) / (7 * DAY_MS));
}

/**
 * Recomputes the offset when switching from view `from` to view `to` so the
 * calendar stays on the period the user was looking at.
 *
 * Week and workweek share a unit (weeks), so toggling between them keeps the
 * offset untouched — the user stays on the same week. Switching into or out of
 * day view changes the unit (days vs weeks); we anchor on a date inside the
 * current period and re-express it in the target unit, so a day-view jump lands
 * on a day the user was already seeing rather than resetting to today.
 */
export function offsetForView(
    today: Date,
    offset: number,
    from: CalendarView,
    to: CalendarView,
): number {
    if (from === to) return offset;
    if (from !== 'day' && to !== 'day') return offset;
    return offsetForDate(today, periodDate(today, offset, from), to);
}

/** `anchor + offset` days. Only meaningful in day-view context. */
export function dayDate(anchor: Date, offset: number): Date {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + offset);
    return d;
}

/** Formats a Date as `YYYY-MM-DD` in local time. Server dates are date-only. */
export function toIsoDate(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * Converts `(h:m, durMin)` to the 15-minute slot bounds used by the
 * grid renderer. `startHour` is the first visible hour of the grid
 * (e.g. 7 when `calStartHour === 7`).
 */
function to15MinSlot(item: LayoutInput, startHour: number) {
    const startSlot = (item.startH - startHour) * 4 + Math.floor(item.startM / 15);
    const heightSlots = Math.max(1, Math.ceil(item.dur / 15));
    return { startSlot, heightSlots, endSlot: startSlot + heightSlots };
}

export interface LanedItem<T extends LayoutInput> {
    sess: T;
    startSlot: number;
    endSlot: number;
    heightSlots: number;
    /** 0-indexed column within the day cell. */
    lane: number;
    /** Total lanes in this item's overlap group (= width divisor). */
    laneCount: number;
}

/**
 * Assigns overlapping sessions to side-by-side lanes so the renderer can
 * draw them at `left: laneIndex / laneCount, width: 1 / laneCount`.
 *
 * Ported from the legacy greedy algorithm in [client-legacy/js/calendar.js]
 * `schedLayoutLanes`. The key property is that `laneCount` is computed per
 * overlap group, not globally — a lone afternoon session doesn't get
 * squished just because two morning sessions overlapped.
 *
 * Items whose start falls outside [startHour, endHour] are dropped. Items
 * whose end extends past `endHour` are kept (tall block rendering), but
 * a fully out-of-range item is excluded.
 */
export function layoutLanes<T extends LayoutInput>(
    sessions: readonly T[],
    startHour: number,
    endHour: number,
): LanedItem<T>[] {
    const total15 = (endHour - startHour) * 4;
    const items: LanedItem<T>[] = [];

    for (const sess of sessions) {
        const { startSlot, heightSlots, endSlot } = to15MinSlot(sess, startHour);
        if (startSlot < 0 || startSlot >= total15) continue;
        items.push({ sess, startSlot, heightSlots, endSlot, lane: 0, laneCount: 1 });
    }

    items.sort((a, b) =>
        a.startSlot !== b.startSlot
            ? a.startSlot - b.startSlot
            : b.heightSlots - a.heightSlots,
    );

    // Greedy lane assignment: place each item in the first lane whose
    // last-placed item ended on or before this item's start.
    const laneEnds: number[] = [];
    for (const item of items) {
        let assigned = laneEnds.findIndex((end) => item.startSlot >= end);
        if (assigned === -1) {
            assigned = laneEnds.length;
            laneEnds.push(0);
        }
        item.lane = assigned;
        laneEnds[assigned] = item.endSlot;
    }

    // laneCount per item = max lane across its overlap cluster + 1.
    //
    // Sweep-line over items (already sorted by startSlot): a new cluster
    // begins when an item's startSlot is ≥ the running max endSlot, i.e.
    // no item we've seen so far extends into it. Everything in the same
    // cluster is transitively overlapping (A overlaps B, B overlaps C ⇒
    // A and C share a cluster even if they don't overlap directly), so
    // they all need the same width divisor. O(n) where the old nested
    // loop was O(n²); on a heavy day (≈60 sessions) this changes the
    // per-render cost noticeably.
    let clusterStart = 0;
    let clusterEnd = 0;
    let clusterMaxLane = -1;
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.startSlot >= clusterEnd) {
            for (let j = clusterStart; j < i; j++) items[j].laneCount = clusterMaxLane + 1;
            clusterStart = i;
            clusterEnd = item.endSlot;
            clusterMaxLane = item.lane;
        } else {
            if (item.endSlot > clusterEnd) clusterEnd = item.endSlot;
            if (item.lane > clusterMaxLane) clusterMaxLane = item.lane;
        }
    }
    for (let j = clusterStart; j < items.length; j++) items[j].laneCount = clusterMaxLane + 1;

    return items;
}

/**
 * Rounds a minute-of-day value to the nearest lower 15-minute boundary —
 * used by drag-to-reschedule to snap a drop target.
 */
export function snapToQuarterHour(minuteOfDay: number): number {
    return Math.floor(minuteOfDay / 15) * 15;
}

/*
 * Recurrence model for the React rebuild.
 *
 * The legacy app used fixed counts (26 weekly, 130 daily) and exposed
 * three canned patterns: Weekly / Bi-weekly / Daily (M-F). Per the
 * 2026-04-18 stakeholder decision, the new model is:
 *
 *   - End-date-driven instead of fixed occurrence counts (admins asked
 *     for "schedule through the auth end date").
 *   - Two pattern types:
 *       * Weekly — step N weeks (weekInterval=1 weekly, 2 bi-weekly, …).
 *       * Daily  — an explicit set of weekdays, Sun(0)…Sat(6). Clients
 *         with Mon/Wed/Fri sessions are one series, not three.
 *   - Saturday and Sunday are selectable (the legacy app didn't allow
 *     weekends in the daily generator).
 *
 * `daysOfWeek` uses JS's native `Date.getDay()` convention (0 = Sun),
 * matching the `recurring_series.days_of_week` column on the server.
 * The pattern is persisted structurally on the `recurring_series`
 * table, joined onto each session row — no string parsing needed to
 * reconstruct the cadence.
 */
export type RecurrenceSpec =
    | { type: 'weekly'; weekInterval: number }
    | { type: 'daily'; daysOfWeek: readonly number[] };

/**
 * Generates the ISO dates for a series from a start date through (and
 * including) an end date under the given recurrence spec. Returns an
 * empty array when start > end, when inputs can't be parsed, or when
 * the spec is structurally invalid (weekInterval < 1, empty daysOfWeek).
 * Inputs are treated as calendar dates in local time — the caller
 * combines them with `startH/startM` to form full session records.
 */
export function generateSeriesDates(
    startDate: string,
    endDate: string,
    spec: RecurrenceSpec,
): string[] {
    const start = new Date(`${startDate}T12:00:00`);
    const end = new Date(`${endDate}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    if (start > end) return [];

    const dates: string[] = [];
    const cur = new Date(start);

    if (spec.type === 'weekly') {
        if (!Number.isInteger(spec.weekInterval) || spec.weekInterval < 1) return [];
        const stepDays = spec.weekInterval * 7;
        while (cur <= end) {
            dates.push(toIsoDate(cur));
            cur.setDate(cur.getDate() + stepDays);
        }
        return dates;
    }

    // Daily: step one day at a time, include only selected weekdays.
    if (!spec.daysOfWeek.length) return [];
    const wanted = new Set(spec.daysOfWeek);
    while (cur <= end) {
        if (wanted.has(cur.getDay())) dates.push(toIsoDate(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

/** Mon=0 … Sun=6 — the convention the legacy calendar grid still uses. */
export function dayOfWeekMon0(d: Date): number {
    return (d.getDay() + 6) % 7;
}
